import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Form, Input, Modal } from 'antd';

import ImpactReport from './ImpactReport';
import { KeptNames, OffChoiceRow, useKeptNames } from './ItemTagOffParts';
import { InputIconSelect } from '../Input';
import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import { getTagReferences } from '../../services/tagReferences';
import { turnOffItemTag } from '../../services/itemTags';
import { formatCount } from '../../utils/counts';
import { normalizeId } from '../../utils/ids';
import {
	cleanupFor,
	defaultTagNameFor,
	itemTagRefusal,
	offOutcome,
} from '../../utils/itemTagSettings';
import { TAG_LIMITS, TAG_SEPARATOR } from '../../utils/tags';

// ⚑ How long an edited name, icon or color holds still before the name it
// publishes is asked for again (a dry run; rate class `expensive`).
const NAME_SETTLE_MS = 300;

const NO_TOTALS = Object.freeze({ loading: false, error: null, value: null });

/** A refusal that is about the name typed: it belongs on the Name field. */
const nameRefused = error => {
	const data = error?.response?.data || {};
	return data.code === 'tag_name_taken' || (data.code === 'invalid_tag' && (data.field === 'name' || !data.field));
};

/**
 * STOP USING AS A TAG — one item's off dialog (docs/plans/tags.md §3.2,
 * ruled d30, d33, d36, d37; owner call B19; keepr-api `POST
 * /api/items/{id}/own-tag/disable`; build step T4d and its API follow-ups).
 * Offered from the item's ⋯ and its read view, to a manager of the item's
 * collection, on a card in Chosen items mode (d35b, d39).
 *
 * Opening it asks for its totals (a dry run with no choices): how many items
 * the tag is on, and how many of those by a rule and by hand — the counts
 * the card-wide Off shows, in the same two rows (tags/ItemTagOffParts.js).
 * Two choices, each **Remove** or **Keep** (d30): what happens to the tag's
 * applications by a rule, and to those put on by hand.
 *
 * - **Keeping either CONVERTS** the tag into an ordinary collection tag, its
 *   id kept (pins and filters keep working). Its name, icon and color START
 *   as the item's title and its card's icon and color, and can be changed
 *   here before converting (d36). The name it publishes is asked of the API
 *   first — a dry run with the choices (and the name, icon and color when
 *   changed) answers `names[0].name` and `confirmNames` — and the dialog
 *   says that name and to whom it becomes visible, and that anyone's private
 *   uses of it are removed, without a count, since other people's private
 *   tagging is theirs. The real call sends the same body plus that digest;
 *   409 `confirm_names_required` (a retitle since) shows the name again.
 * - **Removing both DELETES** it ("fresh", d33): using the item as a tag again
 *   later makes a new tag. So the deletion first shows THE IMPACT REPORT
 *   (`GET /api/tags/{id}/references`), with **Remove for me** / **Leave it**
 *   per entry, and the choices ride the request as `cleanup`. No digest: it
 *   publishes no name.
 *
 * ⚑ Neither choice is made for the person: both start unchosen, and the
 * button waits for them. ⚑ An edited name, icon or color is asked about again
 * once it has held still (NAME_SETTLE_MS); until the answer matches what
 * would be sent, the button waits. ⚑ Counts that cannot be read say so and
 * do not block. ⚑ A 409 on the digest shows the name again under "The name
 * changed since you looked", with no second refusal line.
 *
 * `onDone(result, { outcome, name })` hears the API's answer (`{ converted,
 * deleted, applications, privateUsesRemoved, cleanup }`); the dialog closes.
 * `onGone()` hears that the item was no longer a tag (404 `no_item_tag`).
 */
export default function StopItemTagModal({
	open,
	onClose,
	item,
	card = null,
	title = '',
	cardType = 'Item',
	onDone,
	onGone,
}) {
	const itemId = normalizeId(item?._id);
	const tagId = normalizeId(item?.ownTagId);
	const defaultName = useMemo(() => defaultTagNameFor(title), [title]);
	const [automatic, setAutomatic] = useState(null);
	const [hand, setHand] = useState(null);
	const [name, setName] = useState(defaultName);
	const [icon, setIcon] = useState(card?.icon || null);
	const [color, setColor] = useState(card?.color || null);
	const [nameError, setNameError] = useState(null);
	const [refusal, setRefusal] = useState(null);
	const [busy, setBusy] = useState(false);
	const [totals, setTotals] = useState(NO_TOTALS);
	const [report, setReport] = useState({ loading: false, loaded: false, error: null, references: [], truncated: false });
	const [choices, setChoices] = useState({});
	const kept = useKeptNames(body => turnOffItemTag(itemId, body));
	const clearNames = kept.clear;
	const loadNames = kept.load;
	const lastChoices = useRef(null);
	const gone = useRef({ onGone, onClose });
	gone.current = { onGone, onClose };

	useEffect(() => {
		if (!open) return;
		setAutomatic(null);
		setHand(null);
		setName(defaultName);
		setIcon(card?.icon || null);
		setColor(card?.color || null);
		setNameError(null);
		setRefusal(null);
		setBusy(false);
		setTotals(NO_TOTALS);
		setReport({ loading: false, loaded: false, error: null, references: [], truncated: false });
		setChoices({});
		clearNames();
		lastChoices.current = null;
	}, [open]); // eslint-disable-line react-hooks/exhaustive-deps

	// The dialog's totals: a dry run with no choices (the T4d follow-up).
	useEffect(() => {
		if (!open || !itemId) return undefined;
		let live = true;
		setTotals({ loading: true, error: null, value: null });
		turnOffItemTag(itemId, { dryRun: true })
			.then(answer => { if (live) setTotals({ loading: false, error: null, value: answer?.totals || null }); })
			.catch(error => {
				if (!live) return;
				if (error?.response?.data?.code === 'no_item_tag') {
					gone.current.onGone?.();
					gone.current.onClose?.();
					return;
				}
				setTotals({ loading: false, error, value: null });
			});
		return () => { live = false; };
	}, [open, itemId]);

	const outcome = offOutcome({ automatic, hand });

	const [reportAttempt, setReportAttempt] = useState(0);
	// The report is asked for once a deletion is what the choices amount to.
	useEffect(() => {
		if (!open || outcome !== 'delete' || report.loaded || report.loading || !tagId) return undefined;
		let live = true;
		setReport(prev => ({ ...prev, loading: true }));
		getTagReferences(tagId)
			.then(answer => { if (live) setReport({ loading: false, loaded: true, error: null, ...answer }); })
			.catch(error => { if (live) setReport({ loading: false, loaded: true, error, references: [], truncated: false }); });
		return () => { live = false; };
	}, [open, outcome, tagId, reportAttempt]); // eslint-disable-line react-hooks/exhaustive-deps

	const trimmed = name.replace(/\s+/g, ' ').trim();
	const localNameError = (() => {
		if (outcome !== 'convert') return null;
		if (!trimmed) return 'Give the tag a name.';
		if (trimmed.normalize('NFKC').includes(TAG_SEPARATOR)) return 'A tag name cannot contain "/" — it separates a tag from the tag above it.';
		if ([...trimmed].length > TAG_LIMITS.name) return `A tag name is at most ${TAG_LIMITS.name} characters.`;
		return null;
	})();

	// What a keep sends — the dry run and the real call alike. Only what the
	// person changed: the API starts each from the item's title and the card's
	// icon and color itself (d36).
	const keepBody = outcome === 'convert'
		? {
			automatic,
			hand,
			...(trimmed !== defaultName ? { name: trimmed } : {}),
			...((icon || null) !== (card?.icon || null) ? { icon: icon || null } : {}),
			...((color || null) !== (card?.color || null) ? { color: color || null } : {}),
		}
		: null;
	const keepKey = keepBody ? JSON.stringify(keepBody) : null;
	const keepRef = useRef(keepBody);
	keepRef.current = keepBody;

	const namesFor = async (options) => {
		const answer = await loadNames({ ...keepRef.current, dryRun: true }, { key: JSON.stringify(keepRef.current), ...options });
		if (answer?.totals) setTotals({ loading: false, error: null, value: answer.totals });
	};

	// The name a keep publishes (B19): asked when the choices say keep, and
	// again once an edited name, icon or color holds still.
	useEffect(() => {
		if (!open || !keepKey || localNameError) {
			clearNames();
			return undefined;
		}
		const choiceKey = `${automatic}:${hand}`;
		const delay = lastChoices.current === choiceKey ? NAME_SETTLE_MS : 0;
		lastChoices.current = choiceKey;
		const timer = setTimeout(() => { namesFor(); }, delay);
		return () => clearTimeout(timer);
	}, [open, keepKey, localNameError]); // eslint-disable-line react-hooks/exhaustive-deps

	// The names answer for exactly what would be sent, or it is still coming.
	const namesCurrent = Boolean(kept.names) && kept.names.key === keepKey;
	const shownNames = keepKey && !localNameError
		? (namesCurrent ? kept.names : { loading: true, error: null, list: [], digest: null, again: false, key: keepKey })
		: null;
	const namesNameError = namesCurrent && !kept.names.loading && nameRefused(kept.names.error)
		? itemTagRefusal(kept.names.error, 'That name cannot be used for a tag.')
		: null;
	const fieldError = nameError || localNameError || namesNameError;
	const digest = namesCurrent && !kept.names.loading && !kept.names.error ? kept.names.digest : null;

	const ready = outcome === 'convert'
		? !fieldError && Boolean(digest)
		: outcome === 'delete' ? report.loaded : false;

	const submit = async () => {
		if (!ready || busy) return;
		setBusy(true);
		setRefusal(null);
		try {
			const body = outcome === 'convert'
				? { ...keepBody, confirmNames: digest }
				: { automatic: 'remove', hand: 'remove', cleanup: cleanupFor(report.references, choices) };
			const result = await turnOffItemTag(itemId, body);
			onDone?.(result, { outcome, name: outcome === 'convert' ? (kept.names?.list?.[0]?.name || trimmed) : null });
			onClose?.();
		} catch (error) {
			const data = error?.response?.data || {};
			if (data.code === 'no_item_tag') {
				onGone?.();
				onClose?.();
				return;
			}
			if (data.code === 'confirm_names_required' && outcome === 'convert') {
				// The name moved since it was shown (a retitle): show it again.
				namesFor({ again: true });
				return;
			}
			const text = itemTagRefusal(error, 'The tag could not be turned off. Please try again.');
			if (nameRefused(error)) setNameError(text);
			else setRefusal(text);
		} finally {
			setBusy(false);
		}
	};

	const choose = setter => value => { setter(value); setRefusal(null); };
	const counts = totals.value;
	const items = counts ? Number(counts.items) || 0 : null;
	const onItems = items === null ? null : (items ? `It is on ${formatCount(items, 'item')}.` : 'It is on no items yet.');

	return (
		<Modal
			open={open}
			title={`Stop using “${title || 'this item'}” as a tag`}
			width={600}
			okText={outcome === 'convert' ? 'Keep as a collection tag' : 'Stop using as a tag'}
			okButtonProps={{ disabled: !ready, loading: busy, danger: outcome === 'delete' }}
			onOk={submit}
			onCancel={onClose}
			destroyOnHidden
			className="stop-item-tag"
		>
			<div className="modal-identity">
				<RecordIdentity
					kicker={cardType}
					badge={<RecordBadge kind="item" record={card} title={title || 'Untitled item'} size="sm" />}
				/>
			</div>
			<p className="stop-item-tag__lead" data-testid="stop-item-tag-lead">
				{onItems ? `${onItems} ` : ''}Choose what happens where it is on. Keeping either turns it into an ordinary
				collection tag; removing both deletes it.
			</p>
			{totals.error ? (
				<Alert
					type="warning"
					showIcon
					className="stop-item-tag__totals-error"
					data-testid="stop-item-tag-totals-error"
					message={itemTagRefusal(totals.error, 'How many items it is on could not be counted. You can still choose.')}
				/>
			) : null}
			<OffChoiceRow
				label="Applied by a rule"
				count={counts ? Number(counts.automatic) || 0 : null}
				fallbackHint="Where a rule put it on."
				value={automatic}
				disabled={busy}
				onChange={choose(setAutomatic)}
				testId="choice-automatic"
			/>
			<OffChoiceRow
				label="Put on by hand"
				count={counts ? Number(counts.hand) || 0 : null}
				fallbackHint="Where someone put it on."
				value={hand}
				disabled={busy}
				onChange={choose(setHand)}
				testId="choice-hand"
			/>

			{outcome === 'convert' ? (
				<div className="stop-item-tag__convert" data-testid="stop-item-tag-convert">
					<Form layout="vertical">
						<Form.Item
							label="Name"
							required
							validateStatus={fieldError ? 'error' : undefined}
							help={fieldError || undefined}
						>
							<Input
								value={name}
								maxLength={TAG_LIMITS.name}
								aria-label="Name"
								onChange={event => { setName(event.target.value); setNameError(null); }}
								onPressEnter={submit}
							/>
						</Form.Item>
						<InputIconSelect
							elementName="icon"
							elementLabel="Icon and color"
							value={icon}
							onChange={(_, value) => setIcon(value)}
							colorElementName="color"
							colorValue={color}
							onColorChange={(_, hex) => setColor(hex)}
							recordKind="tag"
						/>
					</Form>
					{shownNames && !namesNameError ? (
						<KeptNames
							names={shownNames}
							className="stop-item-tag__names"
							testId="stop-item-tag-names"
							errorFallback="The name it would get could not be checked. Please try again."
						>
							{list => (
								<Alert
									type="info"
									showIcon
									className="stop-item-tag__disclosure"
									message={`It will be named ${list[0]?.name || trimmed || defaultName}, and everyone who can see this collection’s tags will see that name.`}
									description={[
										'Anyone’s private uses of it are removed.',
										automatic === 'keep' ? 'Where a rule put it on, it stays as if put on by hand: no rule remains.' : null,
										hand === 'remove' ? 'Where someone put it on by hand, it comes off.' : null,
										automatic === 'remove' ? 'Where a rule put it on, it comes off.' : null,
									].filter(Boolean).join(' ')}
								/>
							)}
						</KeptNames>
					) : null}
				</div>
			) : null}

			{outcome === 'delete' ? (
				<div className="stop-item-tag__delete" data-testid="stop-item-tag-delete">
					<p>
						Removing both deletes the tag: it comes off every item. Using this item as a tag again later
						makes a new tag, so what names this one stops matching.
					</p>
					<ImpactReport
						references={report.references}
						truncated={report.truncated}
						loading={report.loading}
						error={report.error}
						onRetry={() => { setReport(prev => ({ ...prev, loaded: false, error: null })); setReportAttempt(n => n + 1); }}
						choices={choices}
						onChange={(key, value) => setChoices(prev => ({ ...prev, [key]: value }))}
						disabled={busy}
						subject="this tag"
					/>
				</div>
			) : null}

			{refusal ? <Alert type="error" showIcon className="stop-item-tag__refusal" message={refusal} data-testid="stop-item-tag-refusal" /> : null}
		</Modal>
	);
}
